import classNames from "classnames";
import { Entry } from "contentful";
import Link from "next/link";
import { useRouter } from "next/router";
import { CSSProperties } from "react";

import BreakpointAwareAsset from "~/components/common/breakpoint-aware-asset";
import Container from "~/components/common/container";
import EscapeNewLine from "~/components/common/escape-new-line";
import Grid from "~/components/common/grid";
import HighlightedText from "~/components/common/highlighted-text";
import { useServerSideProps } from "~/contexts/server-side-props";
import Editorials from "~/types/editorials";
import contentfulUtils from "~/utils/contentful-utils";
import { assertEditorialType, isActiveEntry } from "~/utils/editorial-utils";
import textUtils from "~/utils/text-utils";

import buttonStyles from "../../common/button.module.scss";
import slideStyle from "./hero-slide.module.scss";
import videoStyle from "./hero-video.module.scss";

type Props = {
  entry: Entry<unknown>;
};

export default function HeroVideo(props: Props) {
  const router = useRouter();
  const { serverSideProps } = useServerSideProps();

  if (!isActiveEntry(props.entry)) return null;

  assertEditorialType<Editorials.HeroVideo>(props.entry, "heroVideo");

  const inspectorMode = contentfulUtils.useInspectorMode(props.entry);

  const panelVariant = props.entry.fields.panelVariant;

  const aspectRatioMobile = props.entry.fields.media.fields?.aspectRatioMobile ?? "unset";
  const aspectRatioDesktop = props.entry.fields.media.fields?.aspectRatioDesktop ?? "unset";

  const styleAspectRatio = {
    "--hero-video-aspect-ratio-mobile": aspectRatioMobile,
    "--hero-video-aspect-ratio-desktop": aspectRatioDesktop,
  } as CSSProperties;

  const panelClassName = classNames(
    slideStyle.panel,
    videoStyle.panel,
    aspectRatioMobile !== "unset" ? videoStyle.panelMarginBottomMobile : undefined,
    aspectRatioDesktop !== "unset" ? videoStyle.panelMarginBottomDesktop : undefined,
    props.entry.fields.centerText ? slideStyle.centerText : undefined,
    props.entry.fields.centerPanel ? slideStyle.centerPanel : undefined,
    panelVariant == "black" ? slideStyle.panelVariant : undefined
  );

  // static panel content
  const panelContent = (
    <>
      <h2 {...inspectorMode?.getProps("title")} className={slideStyle.title}>
        <HighlightedText text={props.entry.fields.title} />
      </h2>
      {props.entry.fields.description && (
        <p
          {...inspectorMode?.getProps("description")}
          className={classNames(slideStyle.description, videoStyle.description)}
        >
          <EscapeNewLine text={props.entry.fields.description} />
        </p>
      )}
      {props.entry.fields.link && isActiveEntry(props.entry.fields.link) && (
        <div
          className={classNames(
            buttonStyles.button,
            buttonStyles.linkButton,
            panelVariant == "black" ? buttonStyles["secondary"] : buttonStyles["primary"]
          )}
          {...inspectorMode?.getProps("text")}
        >
          {props.entry.fields.link.fields.text}
        </div>
      )}
    </>
  );

  return (
    <div
      className={classNames(
        slideStyle.container,
        serverSideProps?.page?.contentBelowHeader ? slideStyle.contentBelowHeader : undefined,
        aspectRatioMobile !== "unset" ? slideStyle.autoHeightMobile : undefined,
        aspectRatioDesktop !== "unset" ? slideStyle.autoHeightDesktop : undefined
      )}
    >
      <div
        style={styleAspectRatio}
        className={classNames(
          videoStyle.wrapperSlide,
          aspectRatioMobile !== "unset" ? slideStyle.autoHeightMobile : undefined,
          aspectRatioDesktop !== "unset" ? slideStyle.autoHeightDesktop : undefined
        )}
      >
        <BreakpointAwareAsset
          entry={props.entry.fields.media}
          className={classNames(slideStyle.image, videoStyle.video)}
          fill
          isHeroVideo
          videoConfig={{
            autoPlay: props.entry.fields.autoplay,
            controls: props.entry.fields.showControls,
            loop: props.entry.fields.loop,
            muted: props.entry.fields.muted,
          }}
          noLazy
        />
        <Container className={slideStyle.h100}>
          <Grid className={slideStyle.h100}>
            {/* dynamic wrapper panel */}
            {props.entry.fields.link ? (
              <Link href={textUtils.sanitizeContentfulUrl(props.entry.fields.link, router)} className={panelClassName}>
                {panelContent}
              </Link>
            ) : (
              <div className={panelClassName}>{panelContent}</div>
            )}
          </Grid>
        </Container>
      </div>
    </div>
  );
}
